import clsx from "clsx";
import { ButtonHTMLAttributes, DetailedHTMLProps, forwardRef } from "react";
import { styled } from "styled-components";

type ButtonOptions = {
  color?: "primary" | "secondary";
  purpose?: string;
};

const StyledButton = styled.button`
  height: 40px;
  font-feature-settings: "ss01";
  transition: all 100ms;

  &:hover &:not(&:disabled) {
    opacity: 0.8;
  }

  &:active:not(:disabled) {
    transform: scale(0.98) translateZ(0);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export type ButtonProps = DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> &
  ButtonOptions;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props: ButtonProps, ref) => {
    const {
      type = "button",
      color = "primary",
      purpose = "default",
      className,
      children,
      ...rest
    } = props;
    const merged = clsx(
      "uppercase font-normal text-sm items-center tracking-wide gap-5 text-black border-transparent border-solid border",
      {
        ["bg-black dark:bg-semiWhite text-snow dark:text-black disabled:bg-rock dark:disabled:bg-rock"]:
          color === "primary",
        ["bg-clementine dark:bg-sun  text-black"]: color === "secondary",
        ["bg-rock"]: rest.disabled,
      },
      className
    );

    return (
      <StyledButton 
        type={type} 
        className={merged} 
        ref={ref} 
        data-testid={`btn-button-${purpose}-${color}`}
        {...rest}
      >
        {children}
      </StyledButton>
    );
  }
);

Button.displayName = "Button";

/*
IMPROVEMENT SUGGESTIONS:
1. Consider adding size variants (e.g., "small", "medium", "large") to the ButtonOptions type for more flexibility
2. Implement a11y improvements such as aria-pressed for toggle buttons
3. Add loading state support with a spinner component for asynchronous actions
4. Add icon support (left/right positioned icons)
5. Fixed the hover selector syntax from `&:hover &:not(&:disabled)` to `&:hover:not(:disabled)`
6. Add more semantic color variants beyond primary/secondary (e.g., success, danger, warning)
7. Implemented the standardized data-testid format following btn-${context}-${action} pattern
8. Added support for specifying the button context and action for more semantic test identifiers
*/
